import { useCallback } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { AddAlt } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  CircularLoadingButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import {
  FileUploader,
  TextArea,
  type BaseFile,
  type FileUploaderV2RefType,
} from '@mint-lib/tw-components/patterns';

import {
  FILE_FORMATS,
  MAX_FILES,
  MAX_SIZE_BYTES,
} from '../../constants/uploadingFiles.js';
import { useUploadDraft } from '../../hooks/knowledge-base/useUploadDraft.js';

type Props = {
  isOpen: boolean;
  existingFileNames: string[];
  onClose: () => void;
  targetFolderId: string | null;
  targetFolderName?: string;
  initialFiles?: BaseFile[];
};

const UploadFileDialog = ({
  isOpen,
  onClose,
  existingFileNames,
  targetFolderId,
  targetFolderName,
  initialFiles,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', ['aiFileSystem']);
  const {
    uploaderRef,
    setFiles,
    description,
    setDescription,
    validate,
    uploadFile,
    upload,
    isReady,
    isPending,
  } = useUploadDraft({
    existingFileNames,
    folderId: targetFolderId,
  });

  const attachUploader = useCallback(
    (uploader: FileUploaderV2RefType | null) => {
      uploaderRef.current = uploader;

      if (uploader && initialFiles?.length) uploader.setFiles(initialFiles);
    },
    [uploaderRef, initialFiles],
  );

  const isMissingDestination = ff.aiFileSystem.enabled && !targetFolderId;

  const handleUploadFile = async () => {
    if (isMissingDestination) return;

    const { uploaded, failed } = await upload();

    if (uploaded && targetFolderName) {
      enqueueSnackbar({
        variant: 'info',
        subTitle: t('Uploaded to {{folderName}}', {
          folderName: targetFolderName,
        }),
        removable: true,
      });
    }

    if (!failed) onClose();
  };

  const isUploadBlocked = isPending || !isReady || isMissingDestination;

  return (
    <Dialog modal onOpenChange={onClose} open={isOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          aria-describedby={undefined}
          size="s"
          className="max-h-[90vh]"
        >
          <DialogHeader>
            <DialogTitle>{t('Upload file')}</DialogTitle>
          </DialogHeader>
          <DialogContent className="flex min-h-0 flex-col overflow-y-auto">
            <FileUploader
              ref={attachUploader}
              fullWidth
              accept={FILE_FORMATS}
              validate={validate}
              onChange={setFiles}
              onUpload={uploadFile}
              maxFiles={MAX_FILES}
              maxSize={MAX_SIZE_BYTES}
            />

            <div className="mt-4 flex">
              <TextArea
                label={t('Description (Optional)')}
                placeholder={t('Insert a description')}
                onChange={({ target }) => setDescription(target.value)}
                value={description}
              />
            </div>
          </DialogContent>
          <DialogFooter>
            {ff.aiFileSystem.enabled ? (
              <>
                <Button size="large" variant="plain" onClick={onClose}>
                  {t('Cancel')}
                </Button>
                <Button
                  size="large"
                  onClick={handleUploadFile}
                  disabled={isUploadBlocked}
                >
                  {isPending && (
                    <CircularLoadingButton size="small" variant="outlined" />
                  )}
                  {t('Upload')}
                </Button>
              </>
            ) : (
              <Button onClick={handleUploadFile} disabled={isUploadBlocked}>
                {isPending ? (
                  <CircularLoadingButton size="small" variant="outlined" />
                ) : (
                  <AddAlt />
                )}
                {t('Confirm')}
              </Button>
            )}
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { UploadFileDialog };
